import React, { useContext } from 'react';
import {
  Button,
  Image,
  Link,
  Text,
  Title3,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useAcceptInvitationStyles } from '../styles';
import GoogleIcon from '../../../media/btn-google.svg';
import HeaderLogo from '../../../media/microsoft-advertising-logo-idp-header.png';

// "Learn more" target for the sign-in explainer. Same Microsoft Advertising help host the pre-auth
// landing page uses for its sign-in help link.
const LEARN_MORE_URL = 'https://help.ads.microsoft.com/#apex/ads/en/60259/0';

export type ActivationGoogleAccountRequiredProps = {
  // The Google account email this invitation's user is registered with. Shown inline so the user
  // knows which account to sign in with. Optional — omitted from the copy when unavailable.
  email?: string | null;
  // "Sign in with Google" — sign the current MSA/AAD session out and send the user to Google sign-in.
  onSignInWithGoogle: () => void;
  // "Go back" — sign the current MSA/AAD session out and return to the default invitation choice.
  onGoBack: () => void;
};

// Shown when ActivationOptions reports the invited email is registered with a Google account, but
// the user signed in with a personal Microsoft (MSA) or work/school (AAD) account. To accept the
// invitation they must re-authenticate with Google — both actions sign the current session out
// first (the URLs are resolved by the caller).
export const ActivationGoogleAccountRequired: React.FC<ActivationGoogleAccountRequiredProps> = ({
  email,
  onSignInWithGoogle,
  onGoBack,
}) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useAcceptInvitationStyles();

  const accountLine = email
    ? i18n.getString(
      _TL_('Your Microsoft Advertising account for {{email}} uses Google sign-in.'),
      { email }
    )
    : i18n.getString(_TL_('Your Microsoft Advertising account uses Google sign-in.'));

  return (
    <div className={classes.centeredPage}>
      <div
        className={classes.errorCard}
        role="region"
        aria-labelledby="activation-google-required-title"
        aria-describedby="activation-google-required-description"
      >
        <Image className={classes.brandLogo} src={HeaderLogo} alt={i18n.getString(_TL_('Microsoft Advertising'))} />

        <Title3 id="activation-google-required-title" className={classes.errorTitle}>
          {i18n.getString(_TL_('Sign in with your Google account'))}
        </Title3>

        <div id="activation-google-required-description" className={classes.errorBody}>
          <Text block>{accountLine}</Text>
          <Text block>
            {i18n.getString(_TL_('To continue, sign in with your Google account.'))}
            {' '}
            <Link href={LEARN_MORE_URL} target="_blank" rel="noopener noreferrer">
              {i18n.getString(_TL_('Learn More'))}
            </Link>
          </Text>
        </div>

        <div className={classes.errorActions}>
          <Button
            className="google-account-continue"
            appearance="primary"
            icon={<Image src={GoogleIcon} width={16} height={16} alt="" aria-hidden="true" />}
            onClick={onSignInWithGoogle}
          >
            {i18n.getString(_TL_('Sign in with Google'))}
          </Button>
          <Button
            className="google-account-go-back"
            appearance="secondary"
            onClick={onGoBack}
          >
            {i18n.getString(_TL_('Go back'))}
          </Button>
        </div>
      </div>
    </div>
  );
};
